'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import {
	CircleCheckBig,
	Store as StoreIcon,
	Plus,
	LayoutDashboard,
	ExternalLink,
	MoreVertical,
	Trash2,
	Calendar,
	Share2,
	Copy,
	Check,
} from 'lucide-react';
import Image from 'next/image';

type Store = {
	_id: string;
	name: string;
	slug: string;
	university?: string;
	description?: string;
	status?: 'pending' | 'approved' | 'rejected' | 'draft';
	createdAt?: string;
	logoUrl?: string;
	referralCode?: string;
};

export default function VendorDashboard() {
	const { user, loading: authLoading } = useAuth(); // Destructure auth loading
	const [stores, setStores] = useState<Store[]>([]);
	const [dataLoading, setDataLoading] = useState(true); // Rename to dataLoading
	const [msg, setMsg] = useState<string | null>(null);
	const [copiedId, setCopiedId] = useState<string | null>(null);
	const router = useRouter();

	useEffect(() => {
		// Only load if auth is done and user exists
		if (!authLoading && user) {
			load();
		} else if (!authLoading && !user) {
			// If auth done and no user, stop data loading (it won't happen)
			setDataLoading(false);
		}
	}, [authLoading, user]); // Depend on auth state

	async function load() {
		setDataLoading(true);
		setMsg(null);
		try {
			const resp = await api.get('/stores/me');
			setStores(resp.data.stores || []);
		} catch (err: any) {
			console.error(err);
			setMsg('Failed to load your stores');
		} finally {
			setDataLoading(false);
		}
	}

	// Helper for status colors
	const getStatusColor = (status: string = 'draft') => {
		switch (status) {
			case 'approved':
				return 'bg-green-100 text-green-700 border-green-200';
			case 'pending':
				return 'bg-yellow-50 text-yellow-700 border-yellow-200';
			case 'rejected':
				return 'bg-red-50 text-red-700 border-red-200';
			default:
				return 'bg-gray-100 text-gray-600 border-gray-200';
		}
	};

	// --- Auth Timeout Fallback ---
	// If authLoading gets stuck, force show the UI after 1.5s
	const [authTimeout, setAuthTimeout] = useState(false);
	useEffect(() => {
		const timer = setTimeout(() => {
			if (authLoading) setAuthTimeout(true);
		}, 1500);
		return () => clearTimeout(timer);
	}, [authLoading]);

	// --- 1. Auth Loading State ---
	// Show spinner only if loading AND haven't timed out
	if (authLoading && !authTimeout) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50/50">
				<div className="animate-pulse flex flex-col items-center">
					<div className="w-12 h-12 bg-gray-200 rounded-full mb-4"></div>
					<div className="h-4 bg-gray-200 rounded w-32"></div>
					<p className="text-sm text-gray-400 mt-4">Verifying access...</p>
				</div>
			</div>
		);
	}

	// --- 2. Not Logged In (or Auth Failed/Timed Out) -> Show Modal ---
	if (!user) {
		return (
			<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
				<div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center animate-in zoom-in-95 duration-200">
					<div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
						<StoreIcon className="w-8 h-8" />
					</div>
					<h2 className="text-2xl font-bold text-[#051449] mb-2">
						Vendor Dashboard
					</h2>
					<p className="text-gray-500 mb-8">
						Please sign in to manage your stores, view
						analytics, and process orders.
					</p>
					<div className="flex flex-col gap-3">
						<button
							onClick={() =>
								router.push(
									'/auth/login?redirect=/vendor/dashboard',
								)
							}
							className="w-full py-3 bg-[#051449] text-white font-bold rounded-xl hover:bg-blue-900 transition-colors"
						>
							Sign In to Continue
						</button>
						<button
							onClick={() => router.push('/')}
							className="w-full py-3 text-gray-500 font-medium hover:bg-gray-50 rounded-xl transition-colors"
						>
							Return Home
						</button>
					</div>
				</div>
			</div>
		);
	}

	// --- 3. Logged In -> Show Dashboard ---
	return (
		<div className="min-h-screen bg-gray-50/50 p-4 md:p-8">
			<div className="max-w-7xl mx-auto">
				{/* Header Section */}
				<div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
					<div>
						<h1 className="text-3xl font-bold text-[#051449] tracking-tight">
							My Stores
						</h1>
						<p className="text-gray-500 mt-1 max-w-lg">
							Manage your business presence across campus.
							Create new outlets and track performance.
						</p>
					</div>

					<div className="flex flex-col sm:flex-row gap-3">
						{!user?.isAlumni && (
							<button
								onClick={() =>
									router.push('/become-alumni')
								}
								className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50 transition-colors shadow-sm"
							>
								<CircleCheckBig className="w-4 h-4 mr-2 text-blue-600" />
								Become Alumni
							</button>
						)}
						<button
							onClick={() => router.push('/vendor/create')}
							className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg font-bold shadow-lg shadow-yellow-500/20 hover:shadow-yellow-500/30 transition-all transform active:scale-95"
							style={{
								background: '#FFCF23',
								color: '#051449',
							}}
						>
							<Plus className="w-5 h-5 mr-1" />
							Create Store
						</button>
					</div>
				</div>

				{msg && (
					<div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center">
						⚠️ {msg}
					</div>
				)}

				{/* Content Grid */}
				{dataLoading ? (
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
						{[1, 2, 3].map((i) => (
							<div
								key={i}
								className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 h-48 animate-pulse"
							>
								<div className="flex gap-4">
									<div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
									<div className="flex-1 space-y-2">
										<div className="h-4 bg-gray-200 rounded w-3/4"></div>
										<div className="h-3 bg-gray-200 rounded w-1/2"></div>
									</div>
								</div>
							</div>
						))}
					</div>
				) : stores.length === 0 ? (
					<div className="bg-white border-2 border-dashed border-gray-200 rounded-xl p-12 flex flex-col items-center justify-center text-center hover:border-blue-200 transition-colors group">
						<div className="w-20 h-20 bg-blue-50 text-blue-200 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-100 transition-colors">
							<StoreIcon className="w-10 h-10" />
						</div>
						<h3 className="text-xl font-bold text-gray-900">
							No stores created yet
						</h3>
						<p className="text-gray-500 mt-2 mb-6 max-w-sm">
							Start selling to students by creating your
							first store. It only takes a minute to set up.
						</p>
						<button
							onClick={() => router.push('/vendor/create')}
							className="px-6 py-3 rounded-lg font-bold transition-transform hover:scale-105"
							style={{
								background: '#FFCF23',
								color: '#051449',
							}}
						>
							Launch Your First Store
						</button>
					</div>
				) : (
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
						{stores.map((s) => (
							<div
								key={s._id}
								className="bg-white group rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-blue-200 transition-all duration-200 overflow-hidden flex flex-col"
							>
								{/* Card Body */}
								<div className="p-5 flex-1">
									<div className="flex items-start gap-4">
										{/* Logo */}
										<div className="relative w-16 h-16 flex-shrink-0 bg-gray-50 rounded-lg border border-gray-100 overflow-hidden">
											{s.logoUrl ? (
												<Image
													src={s.logoUrl}
													alt={s.name}
													fill
													className="object-cover"
												/>
											) : (
												<div className="w-full h-full flex items-center justify-center text-xl font-bold text-gray-300">
													{s.name?.[0]?.toUpperCase() ||
														'S'}
												</div>
											)}
										</div>

										{/* Info */}
										<div className="flex-1 min-w-0">
											<div className="flex items-start justify-between">
												<h3
													className="text-lg font-bold text-gray-900 truncate pr-2"
													title={s.name}
												>
													{s.name}
												</h3>
												<span
													className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${getStatusColor(
														s.status,
													)}`}
												>
													{s.status || 'Draft'}
												</span>
											</div>

											<div className="mt-1 flex items-center text-sm text-gray-500">
												{s.university ||
													'University not set'}
											</div>

											<div className="mt-2 flex items-center gap-2 text-xs text-gray-400">
												<Calendar className="w-3 h-3" />
												<span>
													Created{' '}
													{new Date(
														s.createdAt || Date.now(),
													).toLocaleDateString()}
												</span>
											</div>

											{/* Referral Code */}
											{s.referralCode && (
												<div className="mt-3 flex items-center gap-2">
													<Share2 className="w-3 h-3 text-blue-500" />
													<code className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-mono">
														{s.referralCode}
													</code>
													<button
														type="button"
														title="Copy referral code"
														onClick={(e) => {
															e.stopPropagation();
															navigator.clipboard.writeText(s.referralCode!);
															setCopiedId(s._id);
															setTimeout(() => setCopiedId(null), 2000);
														}}
														className="p-0.5 text-gray-400 hover:text-blue-600 transition-colors"
													>
														{copiedId === s._id ? (
															<Check className="w-3 h-3 text-green-500" />
														) : (
															<Copy className="w-3 h-3" />
														)}
													</button>
												</div>
											)}
										</div>
									</div>

									{user?.isAlumni && (
										<div className="mt-4 flex items-center text-xs font-medium text-blue-700 bg-blue-50 px-3 py-1.5 rounded-md w-fit">
											<CircleCheckBig className="w-3 h-3 mr-1.5" />
											Verified Alumni Business
										</div>
									)}
								</div>

								{/* Card Footer / Actions */}
								<div className="bg-gray-50 p-4 border-t border-gray-100 grid grid-cols-2 gap-3">
									<button
										onClick={() =>
											router.push(`/vendor/stores/${s._id}`)
										}
										className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-blue-600 transition-colors shadow-sm"
									>
										<LayoutDashboard className="w-4 h-4" />
										Dashboard
									</button>
									<button
										onClick={() =>
											router.push(`/stores/${s.slug}`)
										}
										className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-blue-600 transition-colors shadow-sm"
									>
										<ExternalLink className="w-4 h-4" />
										View Store
									</button>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
}
